import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import axios from "axios";
import config from "../../config";
import appToast from '../../utilities/appToast';
import { formatApiError } from '../../utilities/apiErrorMessages';
import { getLanguageHeaders } from "../services/http-common";
import { coalesceAsync } from "../../utilities/coalesceAsync";

export const GetOrders = createAsyncThunk("order/GetOrders", async ({ token, page = 1, perPage, filters = {} }, thunkAPI) => {
    const { rejectWithValue, fulfillWithValue } = thunkAPI
    try {
        const searchParams = new URLSearchParams({ page: String(page ?? 1) });

        if (perPage && Number(perPage) > 0) {
            searchParams.set('per_page', String(perPage));
        }

        Object.entries(filters).forEach(([key, value]) => {
            if (value === undefined || value === null) {
                return;
            }

            if (typeof value === 'string' && value.trim() === '') {
                return;
            }

            searchParams.append(key, value);
        });

        const response = await axios({
            method: "get",
            url: `${config.apiUrl}/orders?${searchParams.toString()}`,
            headers: {
                Accept: "application/json",
                "Content-Type": "application/json",
                Authorization: `Bearer ${token}`,
                ...getLanguageHeaders(),
            }

        })

        if (response.status === 200) {
            return fulfillWithValue(response.data)
        } else {
            return rejectWithValue(response)
        }

    } catch (error) {
        throw rejectWithValue(error.message)
    }
})

export const GetSingleOrder = createAsyncThunk("order/GetSingleOrder", async ({ token, id }, thunkAPI) => {
    const { rejectWithValue, fulfillWithValue } = thunkAPI
    try {
        // Coalesce in-flight requests only: refetches after a mutation must always hit the API.
        const response = await coalesceAsync(`order-show:${id}`, () => axios({
            method: "get",
            url: `${config.apiUrl}/orders/${id}`,
            headers: {
                Accept: "application/json",
                "Content-Type": "application/json",
                Authorization: `Bearer ${token}`,
                ...getLanguageHeaders(),
            }

        }))

        if (response.status === 200) {
            return fulfillWithValue(response.data)
        } else {
            return rejectWithValue(response)
        }

    } catch (error) {
        return rejectWithValue({
            message: error.response?.data?.message ?? error.message,
            status: error.response?.status,
            errors: error.response?.data?.errors,
        })
    }
})

export const GetUnitTypes = createAsyncThunk("order/GetUnitTypes", async (token, thunkAPI) => {
    const { rejectWithValue, fulfillWithValue } = thunkAPI
    try {
        const response = await axios({
            method: "get",
            url: `${config.apiUrl}/unit-types`,
            headers: {
                Accept: "application/json",
                "Content-Type": "application/json",
                Authorization: `Bearer ${token}`,
                ...getLanguageHeaders(),
            }

        })

        if (response.status === 200) {
            return fulfillWithValue(response.data)
        } else {
            return rejectWithValue(response.error)
        }

    } catch (error) {
        throw rejectWithValue(error.message)
    }
})


export const AddOrder = createAsyncThunk("order/AddOrder", async (obj, thunkAPI) => {
    const { rejectWithValue, fulfillWithValue } = thunkAPI
    try {

        const response = await axios({
            method: 'post',
            url: `${config.apiUrl}/orders-store`,
            data: obj.formData,
            headers: {
                'Content-Type': `multipart/form-data`,
                'Authorization': `Bearer ${obj.token}`,
                ...getLanguageHeaders(),
            },
        });


        if (response.status === 201) {
            return fulfillWithValue(response.data)
        } else {
            return rejectWithValue(response)
        }
    } catch (error) {
        const data = error.response?.data;
        const payload = data
            ? { message: data.message, errors: data.errors }
            : { message: error.message ?? 'Failed to create order.' };
        return rejectWithValue(payload);
    }
})


export const UpdateOrder = createAsyncThunk("order/UpdateOrder", async (obj, thunkAPI) => {
    const { rejectWithValue, fulfillWithValue } = thunkAPI
    try {

        const response = await axios({
            method: 'post',
            url: `${config.apiUrl}/orders-update`,
            data: obj.formData,
            headers: {
                'Content-Type': `multipart/form-data`,
                'Authorization': `Bearer ${obj.token}`,
                ...getLanguageHeaders(),
            },
        });

        return fulfillWithValue(response.data);
    } catch (error) {
        return rejectWithValue(error.response?.data ?? { message: error.message ?? 'Failed to update order.' });
    }
});

export const DeleteOrderAttachment = createAsyncThunk("order/DeleteOrderAttachment", async (obj, thunkAPI) => {
    const { rejectWithValue, fulfillWithValue } = thunkAPI;
    try {
        const response = await axios({
            method: 'delete',
            url: `${config.apiUrl}/orders/${obj.id}/attachments/${obj.attachmentId}`,
            headers: {
                Accept: 'application/json',
                'Content-Type': 'application/json',
                Authorization: `Bearer ${obj.token}`,
                ...getLanguageHeaders(),
            },
        });

        return fulfillWithValue(response.data);
    } catch (error) {
        return rejectWithValue(error.response?.data ?? { message: error.message ?? 'Failed to delete attachment' });
    }
});

// Change order status (independent endpoint)
export const ChangeOrderStatus = createAsyncThunk("order/ChangeOrderStatus", async (obj, thunkAPI) => {
    const { rejectWithValue, fulfillWithValue } = thunkAPI
    try {
        const response = await axios({
            method: 'post',
            url: `${config.apiUrl}/orders/${obj.id}/status`,
            data: obj.formData,
            headers: {
                'Content-Type': `multipart/form-data`,
                'Authorization': `Bearer ${obj.token}`,
                ...getLanguageHeaders(),
            },
        });

        if (response.status === 200 || response.status === 201) {
            return fulfillWithValue(response.data)
        }

        return rejectWithValue(response.data ?? { message: 'Failed to change order status.' });

    } catch (error) {
        return rejectWithValue(error.response?.data ?? { message: error.message ?? 'Failed to change order status.' });
    }
})

// Assign technicians (independent endpoint)
export const AssignTechnician = createAsyncThunk("order/AssignTechnician", async (obj, thunkAPI) => {
    const { rejectWithValue, fulfillWithValue } = thunkAPI
    try {
        const payload = obj.formData
            ? obj.formData
            : { technician_ids: Array.isArray(obj.technician_ids) ? obj.technician_ids : [] };
        const response = await axios({
            method: 'post',
            url: `${config.apiUrl}/orders/${obj.id}/technician`,
            data: payload,
            headers: {
                ...(obj.formData ? { 'Content-Type': 'multipart/form-data' } : { 'Content-Type': 'application/json' }),
                'Authorization': `Bearer ${obj.token}`,
                ...getLanguageHeaders(),
            },
        });

        if (response.status === 200 || response.status === 201) {
            return fulfillWithValue(response.data)
        }

        return rejectWithValue(response.data ?? { message: 'Failed to assign technician.' });

    } catch (error) {
        return rejectWithValue(error.response?.data ?? { message: error.message ?? 'Failed to assign technician.' });
    }
})

// Assign courier (independent endpoint)
export const AssignCourier = createAsyncThunk("order/AssignCourier", async (obj, thunkAPI) => {
    const { rejectWithValue, fulfillWithValue } = thunkAPI
    try {
        const response = await axios({
            method: 'post',
            url: `${config.apiUrl}/orders/${obj.id}/courier`,
            data: obj.formData,
            headers: {
                'Content-Type': `multipart/form-data`,
                'Authorization': `Bearer ${obj.token}`,
                ...getLanguageHeaders(),
            },
        });

        if (response.status === 200 || response.status === 201) {
            return fulfillWithValue(response.data)
        }

        return rejectWithValue(response.data ?? { message: 'Failed to assign courier.' });

    } catch (error) {
        return rejectWithValue(error.response?.data ?? { message: error.message ?? 'Failed to assign courier.' });
    }
})


export const DeleteOrder = createAsyncThunk("order/DeleteOrder", async ({ id, token }, thunkAPI) => {
    const { rejectWithValue, fulfillWithValue } = thunkAPI

    try {
        const response = await axios({
            method: 'delete',
            url: `${config.apiUrl}/orders/${id}`,
            headers: {
                Accept: "application/json",
                "Content-Type": "application/json",
                'Authorization': `Bearer ${token}`,
                ...getLanguageHeaders(),
            },
        });

        if (response.status === 200) {
            return fulfillWithValue(response.data)
        } else {
            return rejectWithValue(response)
        }
    } catch (error) {
        return rejectWithValue(error.response?.data ?? { message: error.message ?? 'Request failed.' })
    }
})

export const MarkAsPaid = createAsyncThunk("order/MarkAsPaid", async ({ invoiceId, token }, thunkAPI) => {
    const { rejectWithValue, fulfillWithValue } = thunkAPI


    try {

        const response = await axios({
            method: 'get',
            url: `${config.apiUrl}/order-mark-as-paid/${invoiceId}`,
            headers: {
                Accept: "application/json",
                "Content-Type": "application/json",
                'Authorization': `Bearer ${token}`,
                ...getLanguageHeaders(),
            },
        });

        if (response.status === 200) {
            return fulfillWithValue(response.data)
        } else {
            return rejectWithValue(response)
        }

    } catch (error) {
        return rejectWithValue(error.response?.data ?? { message: error.message ?? 'Request failed.' })
    }
})

export const MarkAsUnPaid = createAsyncThunk("order/MarkAsUnPaid", async ({ invoiceId, token }, thunkAPI) => {
    const { rejectWithValue, fulfillWithValue } = thunkAPI

    try {

        const response = await axios({
            method: 'get',
            url: `${config.apiUrl}/order-mark-as-unpaid/${invoiceId}`,
            headers: {
                Accept: "application/json",
                "Content-Type": "application/json",
                'Authorization': `Bearer ${token}`,
                ...getLanguageHeaders(),
            },
        });

        if (response.status === 200) {
            return fulfillWithValue(response.data)
        } else {
            return rejectWithValue(response)
        }
    } catch (error) {
        return rejectWithValue(error.response?.data ?? { message: error.message ?? 'Request failed.' })
    }
})

export const MarkAsDelivered = createAsyncThunk("order/MarkAsDelivered", async ({ id, token }, thunkAPI) => {
    const { rejectWithValue, fulfillWithValue } = thunkAPI

    try {

        const response = await axios({
            method: 'get',
            url: `${config.apiUrl}/order-set-delivered/${id}`,
            headers: {
                Accept: "application/json",
                "Content-Type": "application/json",
                'Authorization': `Bearer ${token}`,
                ...getLanguageHeaders(),
            },
        });


        if (response.status === 200) {
            return fulfillWithValue(response.data)
        } else {
            return rejectWithValue(response)
        }

    } catch (error) {
        return rejectWithValue(error.response?.data ?? { message: error.message ?? 'Request failed.' })
    }
})

export const SearchOrders = createAsyncThunk("order/SearchOrders", async ({ values, token }, thunkAPI) => {
    const { rejectWithValue, fulfillWithValue } = thunkAPI

    const name = values.name || ""
    const status = values.payment_status || ""
    const color_id = values.color_id || ""
    const dentist_id = values.dentist_id || ""
    const formula_type_id = values.formula_type_id || ""
    const date = values.date || ""
    const date_from = values.date_from || ""
    const date_to = values.date_to || ""
    const delivered = values.delivered || ""

    try {

        const response = await axios({
            method: 'get',
            url: `${config.apiUrl}/orders?patient_name=${name}&payment_status=${status}&color_id=${color_id}&dentist_id=${dentist_id}&formula_type_id=${formula_type_id}&date=${date}&date_from=${date_from}&date_to=${date_to}&delivered=${delivered}`,
            headers: {
                Accept: "application/json",
                "Content-Type": "application/json",
                'Authorization': `Bearer ${token}`,
                ...getLanguageHeaders(),
            },
        });
        if (response.status === 200) {
            return fulfillWithValue(response.data)
        } else {
            return rejectWithValue(response)
        }
    } catch (error) {
        return rejectWithValue(error.response?.data ?? { message: error.message ?? 'Request failed.' })
    }
})

export const OrderSlice = createSlice({
    name: "order",
    initialState: {
        orders: null,
        singleOrder: null,
        selectedTooths: [],
        unitTypes: null,
        ordersAlerts: null,
        loading: false,
        fetchOrders: '',
        fetchSingleOrder: '',
        addOrder: '',
        updateOrder: '',
        deleteOrder: '',
        totalOrders: "",
        successMessege: ""

    },

    reducers: {
        toggleSelect: (state, action) => {
            if (state.selectedTooths.indexOf(action.payload) === -1) {
                state.selectedTooths.push(action.payload)
            }
            else if (state.selectedTooths.indexOf(action.payload !== -1)) {
                state.selectedTooths.splice(state.selectedTooths.indexOf(action.payload), 1)
            }
        },
        setSelectedTooths: (state, action) => {
            state.selectedTooths = Array.isArray(action.payload) ? action.payload : [];
        },
        setUnitTypes: (state, action) => {
            state.unitTypes = Array.isArray(action.payload) ? action.payload : null;
        },
        // Reset the addOrder flag after it's been handled to avoid unintended redirects
        resetAddOrder: (state) => {
            state.addOrder = '';
            state.successMessege = null;
            state.ordersAlerts = null;
        },
        resetUpdateOrder: (state) => {
            state.updateOrder = '';
            state.successMessege = null;
            state.ordersAlerts = null;
        },
        setSingleOrderAttachments: (state, action) => {
            const { orderId, attachments } = action.payload ?? {};
            if (state.singleOrder && Number(state.singleOrder.id) === Number(orderId)) {
                state.singleOrder.attachments = Array.isArray(attachments) ? attachments : [];
            }
        },
    },
    extraReducers: {
        //GetOrders actions
        [GetOrders.pending]: (state, action) => {
            state.loading = true
            state.fetchOrders = ''
            state.selectedTooths = []
            state.ordersAlerts = null
            state.successMessege = null
        },
        [GetOrders.fulfilled]: (state, action) => {
            state.loading = false
            state.orders = action.payload.data
            state.totalOrders = action.payload.data.total
            state.selectedTooths = []
            state.fetchOrders = 'fetch'
            state.ordersAlerts = null
            state.successMessege = null
        },
        [GetOrders.rejected]: (state, action) => {
            state.loading = false
            state.fetchOrders = 'fetch failed'
            state.ordersAlerts = formatApiError(action.payload, 'Request failed.')
            state.successMessege = null
        },

        //SearchOrders actions
        [SearchOrders.pending]: (state, action) => {
            state.loading = true
            state.fetchOrders = ''
            state.selectedTooths = []
            state.successMessege = null
        },
        [SearchOrders.fulfilled]: (state, action) => {
            state.loading = false
            state.orders = action.payload.data
            state.totalOrders = action.payload.data.total
            state.selectedTooths = []
            state.fetchOrders = 'fetch'
            state.successMessege = null
        },
        [SearchOrders.rejected]: (state, action) => {

            state.loading = false
            state.fetchOrders = 'fetch failed'
            state.successMessege = null
        },

        //GetSingleOrder actions
        [GetSingleOrder.pending]: (state, action) => {

            state.loading = true
            state.selectedTooths = []
            // Avoid flashing previous order values into the edit form while fetching.
            const requestId = action?.meta?.arg?.id
            if (
                requestId != null
                && state.singleOrder
                && String(state.singleOrder.id) !== String(requestId)
            ) {
                state.singleOrder = null
            }
            state.ordersAlerts = null
            state.successMessege = null
        },
        [GetSingleOrder.fulfilled]: (state, action) => {
            state.loading = false
            state.singleOrder = action.payload.data
            state.selectedTooths = (action.payload.data.units ?? [])
                .map((unit) => unit?.unit_type?.order)
                .filter((o) => o != null && o !== '' && Number.isFinite(Number(o)) && Number(o) > 0)
                .map((o) => Number(o))
            state.fetchSingleOrder = 'fetch_single_order'
            state.ordersAlerts = null
            state.successMessege = null



        },
        [GetSingleOrder.rejected]: (state, action) => {
            state.loading = false
            state.singleOrder = null
            state.selectedTooths = []
            state.ordersAlerts = formatApiError(action.payload, 'Request failed.')
            state.successMessege = null
        },

        [DeleteOrderAttachment.pending]: (state) => {
            state.loading = true;
            state.ordersAlerts = null;
            state.successMessege = null;
        },
        [DeleteOrderAttachment.fulfilled]: (state, action) => {
            state.loading = false;
            state.ordersAlerts = null;

            if (action.payload?.message) {
                appToast.success(action.payload.message);
            }

            const updated = action.payload.data;
            if (!updated) return;

            // update singleOrder if it matches
            if (state.singleOrder && (state.singleOrder.id === updated.id || state.singleOrder.id === updated.id)) {
                state.singleOrder = updated;
            }

            // update list item if present
            if (state.orders && Array.isArray(state.orders.data)) {
                const idx = state.orders.data.findIndex((o) => o.id === updated.id);
                if (idx !== -1) {
                    state.orders.data[idx] = { ...state.orders.data[idx], ...updated };
                }
            }
        },
        [DeleteOrderAttachment.rejected]: (state, action) => {
            state.loading = false;
            const errorMessage = action.payload ?? action.error?.message ?? 'Failed to delete attachment';
            state.ordersAlerts = errorMessage;
            appToast.error(errorMessage);
        },

        [GetUnitTypes.pending]: (state, action) => {
            state.loading = true
            state.selectedTooths = []
            state.successMessege = null
            state.ordersAlerts = null
        },
        [GetUnitTypes.fulfilled]: (state, action) => {
            state.loading = false
            state.selectedTooths = []
            state.unitTypes = action.payload.data
            state.successMessege = null
            state.ordersAlerts = null
        },
        [GetUnitTypes.rejected]: (state, action) => {

            state.loading = false
            state.successMessege = null
            state.ordersAlerts = null
        },

        //AddOrder actions
        [AddOrder.pending]: (state, action) => {
            state.loading = true
            state.addOrder = ''
            state.successMessege = null
            state.ordersAlerts = null
        },
        [AddOrder.fulfilled]: (state, action) => {
            state.loading = false
            state.addOrder = 'add'
            state.successMessege = action.payload.message
            state.ordersAlerts = null
            if (action.payload?.message) {
                appToast.success(action.payload.message);
            }
        },
        [AddOrder.rejected]: (state, action) => {
            state.loading = false
            state.addOrder = 'add failed'
            state.successMessege = null
            const errorMessage = formatApiError(action.payload, 'Failed to create order.')
            state.ordersAlerts = errorMessage
            appToast.error(errorMessage)
        },

        //UpdateOrder actions
        [UpdateOrder.pending]: (state, action) => {
            state.loading = true
            state.updateOrder = ''
            state.successMessege = null
            state.ordersAlerts = null
        },
        [UpdateOrder.fulfilled]: (state, action) => {
            state.loading = false
            state.updateOrder = 'update'
            state.successMessege = action.payload.message
            state.ordersAlerts = null
            if (action.payload?.data) {
                state.singleOrder = action.payload.data
            }
            if (action.payload?.message) {
                appToast.success(action.payload.message);
            }
        },
        [UpdateOrder.rejected]: (state, action) => {
            state.loading = false
            state.updateOrder = 'update failed'
            state.successMessege = null
            const errorMessage = formatApiError(action.payload, 'Failed to update order.');
            state.ordersAlerts = errorMessage
            appToast.error(errorMessage)
        },

        // ChangeOrderStatus actions
        [ChangeOrderStatus.pending]: (state, action) => {
            state.loading = true
            state.updateOrder = ''
            state.successMessege = null
            state.ordersAlerts = null
        },
        [ChangeOrderStatus.fulfilled]: (state, action) => {
            state.loading = false
            state.updateOrder = 'status'
            state.successMessege = action.payload.message
            state.ordersAlerts = null
            if (action.payload?.data) {
                state.singleOrder = action.payload.data
            }
            if (action.payload?.message) {
                appToast.success(action.payload.message);
            }
        },
        [ChangeOrderStatus.rejected]: (state, action) => {
            state.loading = false
            state.updateOrder = 'update failed'
            state.successMessege = null
            const errorMessage = formatApiError(action.payload, 'Failed to change order status.');
            state.ordersAlerts = errorMessage
            appToast.error(errorMessage)
        },

        // AssignTechnician actions
        [AssignTechnician.pending]: (state, action) => {
            state.loading = true
            state.updateOrder = ''
            state.successMessege = null
            state.ordersAlerts = null
        },
        [AssignTechnician.fulfilled]: (state, action) => {
            state.loading = false
            state.updateOrder = 'update'
            state.successMessege = action.payload.message
            state.ordersAlerts = null
            if (action.payload?.message) {
                appToast.success(action.payload.message);
            }
        },
        [AssignTechnician.rejected]: (state, action) => {
            state.loading = false
            state.updateOrder = 'update failed'
            state.successMessege = null
            const errorMessage = formatApiError(action.payload, 'Failed to assign technician.');
            state.ordersAlerts = errorMessage
            appToast.error(errorMessage)
        },

        // AssignCourier actions
        [AssignCourier.pending]: (state, action) => {
            state.loading = true
            state.updateOrder = ''
            state.successMessege = null
            state.ordersAlerts = null
        },
        [AssignCourier.fulfilled]: (state, action) => {
            state.loading = false
            state.updateOrder = 'update'
            state.successMessege = action.payload.message
            state.ordersAlerts = null
            if (action.payload?.message) {
                appToast.success(action.payload.message);
            }
        },
        [AssignCourier.rejected]: (state, action) => {
            state.loading = false
            state.updateOrder = 'update failed'
            state.successMessege = null
            const errorMessage = formatApiError(action.payload, 'Failed to assign courier.');
            state.ordersAlerts = errorMessage
            appToast.error(errorMessage)
        },

        //DeleteOrder actions
        [DeleteOrder.pending]: (state, action) => {
            state.loading = true
            state.deleteOrder = ''
            state.ordersAlerts = null
            state.successMessege = null
        },
        [DeleteOrder.fulfilled]: (state, action) => {
            state.loading = false
            state.deleteOrder = 'delete'
            state.ordersAlerts = null
            state.successMessege = action.payload.message
            if (action.payload?.message) {
                appToast.success(action.payload.message);
            }
        },
        [DeleteOrder.rejected]: (state, action) => {
            state.loading = false
            state.deleteOrder = 'delete failed'
            const errorMessage = formatApiError(action.payload, 'Failed to delete order.');
            state.ordersAlerts = errorMessage
            state.successMessege = null
            appToast.error(errorMessage)
        },
        //MarkAsPaid actions
        [MarkAsPaid.pending]: (state, action) => {
            state.loading = true
            state.successMessege = null
            state.ordersAlerts = null
        },
        [MarkAsPaid.fulfilled]: (state, action) => {
            state.loading = false
            state.successMessege = action.payload.message
            state.ordersAlerts = null
        },
        [MarkAsPaid.rejected]: (state, action) => {

            state.loading = false
            state.successMessege = null
            state.ordersAlerts = formatApiError(action.payload, 'Request failed.')
        },
        //MarkAsUnPaid actions
        [MarkAsUnPaid.pending]: (state, action) => {
            state.loading = true
            state.successMessege = null
            state.ordersAlerts = null
        },
        [MarkAsUnPaid.fulfilled]: (state, action) => {
            state.loading = false
            state.successMessege = action.payload.message
            state.ordersAlerts = null
        },
        [MarkAsUnPaid.rejected]: (state, action) => {

            state.loading = false
            state.successMessege = null
            state.ordersAlerts = formatApiError(action.payload, 'Request failed.')

        },
        //MarkAsDelivered actions
        [MarkAsDelivered.pending]: (state, action) => {
            state.loading = true
            state.successMessege = null
            state.ordersAlerts = null
        },
        [MarkAsDelivered.fulfilled]: (state, action) => {
            state.loading = false
            state.successMessege = action.payload.data
            state.ordersAlerts = null
        },
        [MarkAsDelivered.rejected]: (state, action) => {
            state.loading = false
            state.successMessege = null
            state.ordersAlerts = formatApiError(action.payload, 'Request failed.')
        },
    }
})

export default OrderSlice.reducer;
export const { toggleSelect, setSelectedTooths, setUnitTypes, resetAddOrder, resetUpdateOrder, setSingleOrderAttachments } = OrderSlice.actions
